import { Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import { useCallback } from 'react'

import { form } from '../../../../formState'
import { RadioGroupValues } from '../../typings'
import { ArchivedCalendar } from './ArchivedCalendar'
import { OperationsCalendar } from './OperationsCalendar'
import { useDateFnsLocale } from './hooks/useDateFnsLocale'
import { CalendarRange, Props, SimulationDateType } from './typings'
import { getRadioGroupTypes } from './utils'

interface ToggleButtonWithTooltipProps {
  value: string
  text: string
  disabled?: boolean
  className?: string
  trackId?: string
  testId?: string
  extraLabel?: string
}

const ToggleButtonWithTooltip = (props: ToggleButtonWithTooltipProps) => {
  const { value, text, disabled, className, trackId, testId, extraLabel } = props
  return (
    <ToggleButton
      value={value}
      disabled={disabled}
      className={className}
      data-testid={testId}
      data-trackid={trackId}
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        padding: '10px 20px',
      }}
    >
      <Tooltip placement="bottom" title={text}>
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            display: 'block',
          }}
        >
          {text}
        </span>
      </Tooltip>
      {extraLabel && <span style={{ display: 'block', fontSize: 'smaller' }}>{extraLabel}</span>}
    </ToggleButton>
  )
}

export function SimulationDatesPicker(props: Props) {
  const { value, today, values, onChange, onChangeType, maxIntervalLength, firstDayNotArchived } =
    props
  const locale = useDateFnsLocale()
  const valueType = value.type
  const radioValues: RadioGroupValues = getRadioGroupTypes()
  const radioGroupValues: RadioGroupValues = values || radioValues

  const handleTypeChange = useCallback(
    (_: unknown, newValue: string | null) => {
      if (newValue) onChangeType(newValue as SimulationDateType)
    },
    [onChangeType],
  )

  const handleOnDateSelected = useCallback(
    (date: Date): void => {
      onChange({ min: date, max: date }, valueType)
    },
    [onChange, valueType],
  )

  const handleOnRangeSelected = useCallback(
    (calendarRange: CalendarRange): void => {
      onChange(calendarRange, valueType)
    },
    [onChange, valueType],
  )

  return (
    <form.Fieldset>
      <ToggleButtonGroup
        size="small"
        color="primary"
        value={value.type}
        exclusive
        onChange={handleTypeChange}
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
        }}
      >
        {radioGroupValues.map(option => (
          <ToggleButtonWithTooltip
            key={option.value.toString()}
            value={option.value.toString()}
            text={option.label}
            disabled={!!option.disabled}
            className={option.className}
            testId={option.trackId}
            trackId={option.trackId}
          />
        ))}
      </ToggleButtonGroup>

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
        <Stack alignItems="center">
          {value.type === 'fromArchived' && (
            <ArchivedCalendar
              selection={value.date}
              onChange={handleOnDateSelected}
              firstDayNotArchived={firstDayNotArchived}
            />
          )}

          {value.type === 'fromOperations' && (
            <OperationsCalendar
              today={today}
              selection={value.dates}
              onChange={handleOnRangeSelected}
              maxIntervalLength={maxIntervalLength}
              firstDayNotArchived={firstDayNotArchived}
            />
          )}
        </Stack>
      </LocalizationProvider>
    </form.Fieldset>
  )
}
